// Formulaire d'onboarding : création de l'entreprise à la première connexion.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatErreur } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Building2 } from "lucide-react";
import DatePicker from "@/components/DatePicker";

const FORMES_JURIDIQUES = ["EI", "EURL", "SARL", "SAS", "SNC", "Association", "Autre"];
const SECTEURS = [
  "Commerce", "Restauration", "BTP et Artisanat", "Prestation de services",
  "Transport", "Agriculture et Pêche", "Hébergement touristique", "Autre",
];

const ETAT_INITIAL = {
  raison_sociale: "", forme_juridique: "", adresse_rue: "", adresse_ville: "",
  adresse_ile: "", telephone: "", email_contact: "", nom_gerant: "", prenom_gerant: "",
  numero_tahiti: "", numero_rcs: "", numero_employeur: "", regime_tva: "",
  assujetti_tva: "", date_debut_activite: "", secteur_activite: "",
};

// Champ texte défini au niveau module (évite tout remontage / perte de focus).
function ChampTexte({ id, libelle, valeur, onChange, type = "text", facultatif = false, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[#1D3D3D] font-semibold text-sm">
        {libelle} {facultatif && <span className="text-[#4A8A8A] font-normal">(facultatif)</span>}
      </Label>
      <Input
        id={id}
        type={type}
        data-testid={`champ-${id}`}
        value={valeur}
        onChange={(e) => onChange(id, e.target.value)}
        placeholder={placeholder}
        className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]"
      />
    </div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { chargerEntreprise } = useAuth();
  const [form, setForm] = useState(ETAT_INITIAL);
  const [erreur, setErreur] = useState("");
  const [chargement, setChargement] = useState(false);

  const maj = (champ, valeur) => setForm((f) => ({ ...f, [champ]: valeur }));

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur("");

    // Vérification des champs obligatoires (RCS et N° Employeur facultatifs)
    const obligatoires = [
      "raison_sociale", "forme_juridique", "adresse_rue", "adresse_ville", "adresse_ile",
      "telephone", "email_contact", "nom_gerant", "prenom_gerant", "numero_tahiti",
      "regime_tva", "assujetti_tva", "date_debut_activite", "secteur_activite",
    ];
    for (const champ of obligatoires) {
      if (!String(form[champ]).trim()) {
        setErreur("Veuillez renseigner tous les champs obligatoires.");
        return;
      }
    }

    setChargement(true);
    try {
      await api.post("/companies", {
        ...form,
        assujetti_tva: form.assujetti_tva === "oui",
        numero_rcs: form.numero_rcs || null,
        numero_employeur: form.numero_employeur || null,
      });
      await chargerEntreprise();
      navigate("/tableau-de-bord");
    } catch (err) {
      setErreur(formatErreur(err.response?.data?.detail));
    } finally {
      setChargement(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8F4F4] py-10 px-4">
      <div className="max-w-3xl mx-auto fenua-fade-up">
        <div className="mb-8 text-center">
          <span className="text-2xl font-black tracking-tight text-[#1D3D3D]">
            Porinetia Compta <span className="text-[#4A8A8A]">AERH</span>
          </span>
        </div>

        <div className="bg-white rounded-xl border border-[#4A8A8A]/20 shadow-sm p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
              <Building2 className="h-6 w-6 text-[#1D3D3D]" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-[#1D3D3D]">Votre entreprise</h1>
              <p className="text-sm text-[#1D3D3D]/70">Ces informations figureront sur tous vos documents.</p>
            </div>
          </div>

          <form onSubmit={soumettre} className="mt-8 space-y-6" data-testid="formulaire-onboarding">
            {erreur && (
              <div data-testid="message-erreur" className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {erreur}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <ChampTexte id="raison_sociale" libelle="Raison sociale" valeur={form.raison_sociale} onChange={maj} placeholder="Ex. Fare Manava SARL" />

              <div className="space-y-1.5">
                <Label className="text-[#1D3D3D] font-semibold text-sm">Forme juridique</Label>
                <Select value={form.forme_juridique} onValueChange={(v) => maj("forme_juridique", v)}>
                  <SelectTrigger data-testid="champ-forme_juridique" className="bg-white border-[#4A8A8A]/30">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {FORMES_JURIDIQUES.map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <ChampTexte id="adresse_rue" libelle="Rue" valeur={form.adresse_rue} onChange={maj} placeholder="Numéro et nom de rue" />
              <ChampTexte id="adresse_ville" libelle="Ville" valeur={form.adresse_ville} onChange={maj} placeholder="Ex. Papeete" />
              <ChampTexte id="adresse_ile" libelle="Île" valeur={form.adresse_ile} onChange={maj} placeholder="Ex. Tahiti" />
              <ChampTexte id="telephone" libelle="Téléphone" valeur={form.telephone} onChange={maj} placeholder="Ex. 40 00 00 00" />
              <ChampTexte id="email_contact" libelle="Email de contact" valeur={form.email_contact} onChange={maj} type="email" placeholder="contact@entreprise.pf" />
              <ChampTexte id="nom_gerant" libelle="Nom du gérant / responsable" valeur={form.nom_gerant} onChange={maj} />
              <ChampTexte id="prenom_gerant" libelle="Prénom du gérant / responsable" valeur={form.prenom_gerant} onChange={maj} />
              <ChampTexte id="numero_tahiti" libelle="Numéro Tahiti" valeur={form.numero_tahiti} onChange={maj} placeholder="N° TAHITI" />
              <ChampTexte id="numero_rcs" libelle="Numéro RCS" valeur={form.numero_rcs} onChange={maj} facultatif />
              <ChampTexte id="numero_employeur" libelle="Numéro Employeur" valeur={form.numero_employeur} onChange={maj} facultatif />

              <div className="space-y-1.5">
                <Label className="text-[#1D3D3D] font-semibold text-sm">Régime TVA</Label>
                <Select value={form.regime_tva} onValueChange={(v) => maj("regime_tva", v)}>
                  <SelectTrigger data-testid="champ-regime_tva" className="bg-white border-[#4A8A8A]/30">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mensuel">Mensuel</SelectItem>
                    <SelectItem value="Trimestriel">Trimestriel</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[#1D3D3D] font-semibold text-sm">Assujetti à la TVA</Label>
                <Select value={form.assujetti_tva} onValueChange={(v) => maj("assujetti_tva", v)}>
                  <SelectTrigger data-testid="champ-assujetti_tva" className="bg-white border-[#4A8A8A]/30">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oui">Oui</SelectItem>
                    <SelectItem value="non">Non</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[#1D3D3D] font-semibold text-sm">Date de début d'activité</Label>
                <DatePicker
                  testId="champ-date_debut_activite"
                  value={form.date_debut_activite}
                  onChange={(v) => maj("date_debut_activite", v)}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[#1D3D3D] font-semibold text-sm">Secteur d'activité</Label>
                <Select value={form.secteur_activite} onValueChange={(v) => maj("secteur_activite", v)}>
                  <SelectTrigger data-testid="champ-secteur_activite" className="bg-white border-[#4A8A8A]/30">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTEURS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button type="submit" data-testid="bouton-creer-entreprise" disabled={chargement}
              className="w-full bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white font-semibold h-12">
              {chargement ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer mon entreprise"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
